import {
  DownOutlined,
  EditOutlined,
  KeyOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Dropdown,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { MenuProps, TableColumnsType } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../api/errors';
import {
  UserType,
  changeUserAccountEmail,
  createUserAccount,
  listUserAccounts,
  resetUserAccountPassword,
  setUserAccountActive,
  updateUserAccountProfile,
} from '../api/userAccounts';
import type { UserAccount, UserAccountWithTemporaryPassword } from '../api/userAccounts';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';
import { TemporaryPasswordModal } from './users/TemporaryPasswordModal';
import { UserRolesModal } from './users/UserRolesModal';

/** Giá trị của biểu mẫu lập tài khoản mới. */
interface CreateFormValues {
  readonly userName: string;
  readonly email: string;
  readonly fullName: string;
  readonly userType: number;
}

/** Giá trị của biểu mẫu sửa tài khoản. */
interface EditFormValues {
  readonly fullName: string;
  readonly email: string;
}

/** Giá trị của biểu mẫu vô hiệu hóa tài khoản. */
interface DeactivateFormValues {
  readonly reason: string;
}

/** Dữ liệu của một trang đã nạp xong, kèm khóa nhận dạng lần nạp đó. */
interface LoadedPage {
  readonly key: string;
  readonly items: readonly UserAccount[];
  readonly totalCount: number;
}

/** Số bản ghi mỗi trang. Khớp với mặc định của máy chủ. */
const pageSize = 25;

/** Độ rộng cột thao tác: vừa đúng ba nút "Sửa", "Vai trò", "Thêm" trên một dòng. */
const actionColumnWidth = 240;

/** Tên hiển thị của bốn loại tài khoản. */
const userTypeOptions = [
  { value: UserType.Employee, label: 'Nhân viên' },
  { value: UserType.Contractor, label: 'Thuê ngoài' },
  { value: UserType.SystemAccount, label: 'Tài khoản tích hợp' },
  { value: UserType.CustomerPortal, label: 'Cổng khách hàng' },
] as const;

/** Tên hiển thị của một loại tài khoản. */
function userTypeLabel(userType: number): string {
  return (
    userTypeOptions.find((option) => option.value === userType)?.label
    ?? `Loại lạ (${String(userType)})`
  );
}

/**
 * Trang quản trị tài khoản người dùng.
 *
 * Ba điểm khác các trang danh mục đã dựng, và cả ba xuất phát từ việc đây là **lối vào hệ thống**:
 *
 * 1. **Không có nút xóa.** Tài khoản đã đăng nhập là đã để lại vết trong nhật ký hệ thống và trong vết
 *    audit của mọi bản ghi người đó sửa. Thứ cần là vô hiệu hóa, và nó bắt buộc ghi lý do.
 *
 * 2. **Không có ô nhập mật khẩu.** Máy chủ tự sinh mật khẩu tạm và trả về đúng một lần. Xem
 *    {@link TemporaryPasswordModal} để biết vì sao hộp hiện mật khẩu được làm chặt như vậy.
 *
 * 3. **Vô hiệu hóa và đặt lại mật khẩu ẩn với chính tài khoản đang đăng nhập.** Máy chủ đã chặn cả
 *    hai, nhưng hiện một thao tác chắc chắn thất bại thì không nên.
 */
export function UserAccountPage(): ReactNode {
  const { user, hasPermission } = useAuth();

  const canCreate = hasPermission(PermissionCodes.UserAccountCreate);
  const canUpdate = hasPermission(PermissionCodes.UserAccountUpdate);
  const canManage = hasPermission(PermissionCodes.UserAccountManage);
  const canResetPassword = hasPermission(PermissionCodes.UserAccountResetPassword);

  const [createForm] = Form.useForm<CreateFormValues>();
  const [editForm] = Form.useForm<EditFormValues>();
  const [deactivateForm] = Form.useForm<DeactivateFormValues>();

  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const [loaded, setLoaded] = useState<LoadedPage | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<UserAccount | null>(null);
  const [deactivatingAccount, setDeactivatingAccount] = useState<UserAccount | null>(null);
  const [rolesAccount, setRolesAccount] = useState<UserAccount | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const [temporaryPasswordResult, setTemporaryPasswordResult] =
    useState<UserAccountWithTemporaryPassword | null>(null);

  const requestKey = useMemo(
    () => JSON.stringify({ searchTerm, includeInactive, pageNumber, reloadToken }),
    [searchTerm, includeInactive, pageNumber, reloadToken],
  );

  const isLoading = loaded?.key !== requestKey && errorMessage === null;

  const reload = useCallback((): void => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const page = await listUserAccounts({
          searchTerm,
          includeInactive,
          pageNumber,
          pageSize,
        });

        if (controller.signal.aborted) {
          return;
        }

        setErrorMessage(null);
        setLoaded({ key: requestKey, items: page.items, totalCount: page.totalCount });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError
            ? error.message
            : 'Không kết nối được tới máy chủ. Kiểm tra lại đường mạng.',
        );
      }
    })();

    return () => {
      controller.abort();
    };
  }, [requestKey, searchTerm, includeInactive, pageNumber]);

  const items = useMemo<readonly UserAccount[]>(() => loaded?.items ?? [], [loaded]);
  const totalCount = loaded?.totalCount ?? 0;

  function openCreateForm(): void {
    createForm.setFieldsValue({
      userName: '',
      email: '',
      fullName: '',
      userType: UserType.Employee,
    });
    setIsCreateOpen(true);
  }

  function openEditForm(account: UserAccount): void {
    setEditingAccount(account);
    editForm.setFieldsValue({ fullName: account.fullName, email: account.email });
  }

  async function handleCreate(values: CreateFormValues): Promise<void> {
    setIsBusy(true);

    try {
      const created = await createUserAccount(values);

      setIsCreateOpen(false);

      // Hiện mật khẩu tạm NGAY, trước khi tải lại danh sách: nếu tải lại trước thì một lỗi mạng ở
      // bước đó sẽ làm mất luôn mật khẩu vừa nhận, và không lấy lại được.
      setTemporaryPasswordResult(created);
      reload();
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không lập được tài khoản.',
      );
    } finally {
      setIsBusy(false);
    }
  }

  async function handleEdit(values: EditFormValues): Promise<void> {
    if (editingAccount === null) {
      return;
    }

    setIsBusy(true);

    try {
      /*
        Hai lời gọi riêng vì máy chủ tách họ tên và thư điện tử thành hai điểm cuối: thư điện tử là
        định danh nên nó phải không trùng, còn họ tên thì không. Chỉ gọi lời gọi thứ hai khi thư điện
        tử thật sự đổi — gọi vô ích sẽ tạo ra một vết sửa không có gì thay đổi.
      */
      await updateUserAccountProfile(editingAccount.id, {
        fullName: values.fullName,
        preferredLanguageCode: editingAccount.preferredLanguageCode,
      });

      if (values.email !== editingAccount.email) {
        await changeUserAccountEmail(editingAccount.id, values.email);
      }

      void message.success('Đã lưu thay đổi.');
      setEditingAccount(null);
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không lưu được thay đổi.',
      );
    } finally {
      setIsBusy(false);

      /*
        Tải lại cả khi thất bại: hai lời gọi không nằm chung một giao dịch, nên họ tên có thể đã lưu
        trong khi đổi thư điện tử bị từ chối (ví dụ trùng với tài khoản khác). Không tải lại thì danh
        sách vẫn hiện họ tên cũ dù máy chủ đã đổi.
      */
      reload();
    }
  }

  async function handleDeactivate(values: DeactivateFormValues): Promise<void> {
    if (deactivatingAccount === null) {
      return;
    }

    setIsBusy(true);

    try {
      await setUserAccountActive(deactivatingAccount.id, false, values.reason);

      void message.success('Đã vô hiệu hóa tài khoản và thu hồi mọi phiên đăng nhập.');
      setDeactivatingAccount(null);
      reload();
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không vô hiệu hóa được tài khoản.',
      );
    } finally {
      setIsBusy(false);
    }
  }

  async function handleActivate(account: UserAccount): Promise<void> {
    try {
      await setUserAccountActive(account.id, true, null);

      void message.success('Đã kích hoạt lại tài khoản.');
      reload();
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không kích hoạt lại được tài khoản.',
      );
    }
  }

  function confirmResetPassword(account: UserAccount): void {
    Modal.confirm({
      title: `Đặt lại mật khẩu của ${account.userName}?`,
      content:
        'Mật khẩu hiện tại sẽ mất hiệu lực ngay, và mọi phiên đăng nhập của người này bị thu hồi — '
        + 'kể cả phiên đang mở trên máy khác. Hệ thống sinh một mật khẩu tạm mới và hiện đúng một lần.',
      okText: 'Đặt lại mật khẩu',
      okButtonProps: { danger: true },
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          const result = await resetUserAccountPassword(account.id);

          setTemporaryPasswordResult(result);
          reload();
        } catch (error) {
          void message.error(
            error instanceof ApiError ? error.message : 'Không đặt lại được mật khẩu.',
          );
        }
      },
    });
  }

  /** Các thao tác ít dùng của một dòng, gom vào nút "Thêm". */
  function moreActionItems(account: UserAccount): NonNullable<MenuProps['items']> {
    const isOwnAccount = account.id === user?.userId;

    return [
      /*
        Ẩn với chính tài khoản đang đăng nhập: máy chủ từ chối tự đặt lại mật khẩu — đổi mật khẩu của
        mình thì đi qua trang đổi mật khẩu, nơi phải nhập mật khẩu cũ.
      */
      ...(canResetPassword && !isOwnAccount
        ? [
            {
              key: 'reset-password',
              icon: <KeyOutlined />,
              label: 'Đặt lại mật khẩu',
              onClick: () => {
                confirmResetPassword(account);
              },
            },
          ]
        : []),

      /*
        Ẩn vô hiệu hóa với chính tài khoản đang đăng nhập. Máy chủ đã chặn — tự vô hiệu hóa là cách tự
        khóa mình ra ngoài hệ thống — nhưng một thao tác chắc chắn thất bại thì không nên hiện.
      */
      ...(canManage && account.isActive && !isOwnAccount
        ? [
            {
              key: 'deactivate',
              label: 'Vô hiệu hóa',
              danger: true,
              onClick: () => {
                setDeactivatingAccount(account);
                deactivateForm.setFieldsValue({ reason: '' });
              },
            },
          ]
        : []),

      ...(canManage && !account.isActive
        ? [
            {
              key: 'activate',
              label: 'Kích hoạt lại',
              onClick: () => {
                void handleActivate(account);
              },
            },
          ]
        : []),
    ];
  }

  /*
    Độ rộng cột cộng lại vừa khung nội dung ở màn 1440px (khoảng 1150px). Trước đây cột thư điện tử
    không có độ rộng và bảng giãn theo nội dung dài nhất, nên cột thao tác bị đẩy ra ngoài mép phải
    và nút cuối rớt dòng. Giờ bảng dùng bố cục cố định: nhãn dài xuống dòng trong ô, thư điện tử dài
    cắt ở cuối, và cột thao tác ghim bên phải nếu màn hẹp hơn.
  */
  const columns: TableColumnsType<UserAccount> = [
    {
      title: 'Tên đăng nhập',
      dataIndex: 'userName',
      width: 140,
      render: (userName: string) => <Typography.Text strong>{userName}</Typography.Text>,
    },
    { title: 'Họ tên', dataIndex: 'fullName', width: 150 },
    {
      title: 'Thư điện tử',
      dataIndex: 'email',
      width: 190,
      /*
        Cắt chữ bằng CSS ngay trên thẻ chứa địa chỉ, KHÔNG dùng chế độ cắt chữ của Typography: chế độ
        đó cắt theo từng khối con nên có lúc bỏ cả khối và chỉ còn "…". Rê chuột vẫn thấy đủ địa chỉ.
      */
      render: (email: string) => (
        <span className="erp-truncate" title={email} style={{ maxWidth: '100%' }}>
          {email}
        </span>
      ),
    },
    {
      title: 'Loại',
      dataIndex: 'userType',
      width: 110,
      render: (userType: number) => userTypeLabel(userType),
    },
    {
      title: 'Vai trò',
      dataIndex: 'roleCodes',
      width: 180,
      render: (roleCodes: readonly string[]) =>
        roleCodes.length === 0 ? (
          <Tooltip title="Tài khoản đăng nhập được nhưng không mở được màn hình nào.">
            <Tag color="orange">Chưa có vai trò</Tag>
          </Tooltip>
        ) : (
          <Space size={4} wrap>
            {roleCodes.map((code) => (
              <Tag key={code}>{code}</Tag>
            ))}
          </Space>
        ),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 140,
      render: (_value: unknown, account: UserAccount) => (
        <Space size={4} wrap>
          {account.isActive ? (
            <Tag color="green">Đang dùng</Tag>
          ) : (
            <Tooltip title={account.deactivationReason ?? 'Không có lý do đã ghi.'}>
              <Tag color="red">Đã vô hiệu hóa</Tag>
            </Tooltip>
          )}

          {/* Nhãn ngắn cho vừa cột; câu đầy đủ nằm ở chú thích khi rê chuột. */}
          {account.mustChangePassword && (
            <Tooltip title="Đang dùng mật khẩu tạm — người này phải đổi mật khẩu ở lần đăng nhập tới.">
              <Tag color="gold">Mật khẩu tạm</Tag>
            </Tooltip>
          )}

          {account.isLockedOutPermanently && <Tag color="magenta">Bị khóa</Tag>}
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: actionColumnWidth,
      fixed: 'right',
      render: (_value: unknown, account: UserAccount) => {
        const moreItems = moreActionItems(account);

        /*
          Chỉ hai thao tác hay dùng nằm ngoài; các thao tác ít dùng và dễ gây hậu quả — đặt lại mật
          khẩu, vô hiệu hóa — gom vào "Thêm" để cột thao tác giữ một dòng và không bấm nhầm.
        */
        return (
          <Space size={4}>
            {canUpdate && account.isActive && (
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => {
                  openEditForm(account);
                }}
              >
                Sửa
              </Button>
            )}

            {canManage && (
              <Button
                size="small"
                icon={<SafetyCertificateOutlined />}
                onClick={() => {
                  setRolesAccount(account);
                }}
              >
                Vai trò
              </Button>
            )}

            {moreItems.length > 0 && (
              <Dropdown menu={{ items: moreItems }} trigger={['click']}>
                <Button size="small" icon={<DownOutlined />} iconPosition="end">
                  Thêm
                </Button>
              </Dropdown>
            )}
          </Space>
        );
      },
    },
  ];

  const tableScrollWidth = columns.reduce(
    (total, column) => total + (typeof column.width === 'number' ? column.width : 0),
    0,
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        Tài khoản người dùng
      </Typography.Title>

      {errorMessage !== null && <Alert type="error" showIcon message={errorMessage} />}

      <Alert
        type="info"
        showIcon
        message="Tài khoản mới cần được gán vai trò mới dùng được"
        description={
          'Hệ thống tự sinh mật khẩu tạm và hiện đúng một lần khi lập tài khoản. Tài khoản chưa có '
          + 'vai trò thì đăng nhập được nhưng không mở được màn hình nào — hãy gán vai trò ngay sau '
          + 'khi lập. Không xóa được tài khoản, chỉ vô hiệu hóa: tên đăng nhập còn nằm trong nhật ký '
          + 'và trong vết sửa của mọi bản ghi người đó đã làm.'
        }
      />

      <Space wrap>
        <Input.Search
          placeholder="Tìm theo tên đăng nhập, họ tên hoặc thư điện tử"
          allowClear
          style={{ width: 320 }}
          onSearch={(value) => {
            setPageNumber(1);
            setSearchTerm(value);
          }}
        />

        <Space size={4}>
          <Switch
            checked={includeInactive}
            onChange={(checked) => {
              setPageNumber(1);
              setIncludeInactive(checked);
            }}
          />
          <Typography.Text>Hiện cả tài khoản đã vô hiệu hóa</Typography.Text>
        </Space>

        <Button icon={<ReloadOutlined />} onClick={reload}>
          Tải lại
        </Button>

        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateForm}>
            Lập tài khoản
          </Button>
        )}
      </Space>

      <Table<UserAccount>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={[...items]}
        loading={isLoading}
        tableLayout="fixed"
        scroll={{ x: tableScrollWidth }}
        pagination={{
          current: pageNumber,
          pageSize,
          total: totalCount,
          showSizeChanger: false,
          showTotal: (total, range) =>
            `${range[0].toLocaleString('vi-VN')}–${range[1].toLocaleString('vi-VN')}`
            + ` trong ${total.toLocaleString('vi-VN')}`,
          onChange: setPageNumber,
        }}
      />

      <Modal
        title="Lập tài khoản mới"
        open={isCreateOpen}
        confirmLoading={isBusy}
        onOk={() => {
          createForm.submit();
        }}
        onCancel={() => {
          setIsCreateOpen(false);
        }}
        okText="Lập tài khoản"
        cancelText="Hủy"
        destroyOnHidden
      >
        <Form<CreateFormValues>
          form={createForm}
          layout="vertical"
          requiredMark={false}
          disabled={isBusy}
          onFinish={(values) => {
            void handleCreate(values);
          }}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Không nhập mật khẩu"
            description={
              'Hệ thống tự sinh mật khẩu tạm và hiện nó đúng một lần sau khi lưu. Người dùng bị buộc '
              + 'đổi mật khẩu ở lần đăng nhập đầu tiên.'
            }
          />

          <Form.Item
            name="userName"
            label="Tên đăng nhập"
            extra="Chữ thường không dấu, chữ số, dấu chấm, gạch nối. Bắt đầu bằng chữ cái. Ví dụ: nguyen.van.a. KHÔNG đổi được sau khi lập."
            rules={[{ required: true, message: 'Chưa nhập tên đăng nhập.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="fullName"
            label="Họ tên"
            extra="Hiển thị trên chứng từ và trong lịch sử duyệt."
            rules={[{ required: true, message: 'Chưa nhập họ tên.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Thư điện tử"
            rules={[{ required: true, message: 'Chưa nhập thư điện tử.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="userType"
            label="Loại tài khoản"
            extra="Tài khoản tích hợp dành cho tiến trình tự động, không bật được xác thực hai lớp."
            rules={[{ required: true, message: 'Chưa chọn loại tài khoản.' }]}
          >
            <Select<number> options={[...userTypeOptions]} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingAccount === null ? 'Sửa tài khoản' : `Sửa tài khoản ${editingAccount.userName}`}
        open={editingAccount !== null}
        confirmLoading={isBusy}
        onOk={() => {
          editForm.submit();
        }}
        onCancel={() => {
          setEditingAccount(null);
        }}
        okText="Lưu"
        cancelText="Hủy"
        destroyOnHidden
      >
        <Form<EditFormValues>
          form={editForm}
          layout="vertical"
          requiredMark={false}
          disabled={isBusy}
          onFinish={(values) => {
            void handleEdit(values);
          }}
        >
          <Form.Item
            name="fullName"
            label="Họ tên"
            rules={[{ required: true, message: 'Chưa nhập họ tên.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Thư điện tử"
            rules={[{ required: true, message: 'Chưa nhập thư điện tử.' }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          deactivatingAccount === null
            ? 'Vô hiệu hóa tài khoản'
            : `Vô hiệu hóa tài khoản ${deactivatingAccount.userName}`
        }
        open={deactivatingAccount !== null}
        confirmLoading={isBusy}
        onOk={() => {
          deactivateForm.submit();
        }}
        onCancel={() => {
          setDeactivatingAccount(null);
        }}
        okText="Vô hiệu hóa"
        okButtonProps={{ danger: true }}
        cancelText="Hủy"
        destroyOnHidden
      >
        <Form<DeactivateFormValues>
          form={deactivateForm}
          layout="vertical"
          requiredMark={false}
          disabled={isBusy}
          onFinish={(values) => {
            void handleDeactivate(values);
          }}
        >
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Mọi phiên đăng nhập của người này bị thu hồi ngay"
            description={
              'Kể cả phiên đang mở trên máy khác. Tài khoản không bị xóa nên mọi vết sửa và nhật ký '
              + 'của người này vẫn tra lại được, và kích hoạt lại được sau này.'
            }
          />

          <Form.Item
            name="reason"
            label="Lý do vô hiệu hóa"
            extra="Bắt buộc: người bị chặn đăng nhập cần biết vì sao, và người quyết định cần chịu trách nhiệm."
            rules={[{ required: true, message: 'Chưa ghi lý do vô hiệu hóa.' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <UserRolesModal
        account={rolesAccount}
        canManage={canManage}
        onChanged={(updated) => {
          setRolesAccount(updated);
          reload();
        }}
        onClose={() => {
          setRolesAccount(null);
        }}
      />

      <TemporaryPasswordModal
        result={temporaryPasswordResult}
        onClose={() => {
          setTemporaryPasswordResult(null);
        }}
      />
    </div>
  );
}
